import { compact, head, isEmpty, uniq } from 'lodash/fp';
import { useSelector } from 'react-redux';

import { CONSIDERATION_CONTENT_SLUGS } from '../../../src/client/ahm-constants/ui';
import {
  ConsiderationContent,
  Offer,
  Placeholder,
  TableOfContentsItem,
} from '../../../src/client/types/__codegen__/sanity/graphql';
import { GET_CONSIDERATION_CONTENT } from '../../../src/client/pages/consideration-landing/queries/get-consideration-content/query.gql';
import { GET_TOC_CONTACTS } from '../../../src/client/models/table-of-contents/queries.gql';
import { getPreviewOptionsFromCookieHeader } from '../../../src/client/cms/utils/preview-cookie';
import { nextReduxWrapper } from '../../../src/client/store/create-store';
import { SLUG } from '../../../src/client/models/table-of-contents/constants';
import ConsiderationLandingPage from '../../../src/client/pages/consideration-landing';
import fetchProductsFromParams from '../../../src/client/models/product/fetch-products-from-params';
import filterOffersByProductType from '../../../src/client/utils/filter-offers-by-product-type';
import getCmsData from '../../../src/client/cms/utils/get-cms-data';
import getContentToRender from '../../../src/client/pages/consideration-landing/utils/get-content-to-render';
import getCustomSections from '../../../src/client/pages/consideration-landing/utils/get-content-to-render/get-custom-sections';
import getOffersContent from '../../../pages/utils/get-offers-content';
import getSaleableProductIdsFromProductListSections from '../../../src/client/cms/utils/get-saleable-product-ids-from-product-list-sections';
import getSectionsByTypename from '../../../src/client/cms/utils/get-sections-by-typename';
import getShouldSkipClientSideRequest from '../../../src/client/utils/get-should-skip-client-side-request';
import selectHasPartnerReferrer from '../../../src/client/selectors/select-has-partner-referrer';
import selectReferralCode from '../../../src/client/selectors/select-referral-code';
import selectTargetSegment from '../../../src/client/selectors/select-target-segment';
import setCacheControlHeader from '../../../pages/utils/set-cache-control-header';
import useGetConsiderationContent from './utils/use-get-consideration-content';
import useGetOffersContent from '../../../pages/utils/use-get-offers-content';
import useTableOfContents from '../../../src/client/cms/utils/use-table-of-contents';
import type { ConsiderationPageSlug } from '../../../src/client/pages/consideration-landing/types';

const getContactTitle = (slug: ConsiderationPageSlug) =>
  slug === CONSIDERATION_CONTENT_SLUGS.FAMILY_COVER_LANDING_PAGE
    ? 'Need help choosing cover for your family?'
    : 'Need help choosing a cover?';

export type ConsiderationPageData = {
  considerationContent: ConsiderationContent;
  contact?: { items: TableOfContentsItem[]; name: string };
};

export type ConsiderationLandingPageProps = {
  activeOffers: Offer[];
  placeholders: Placeholder[];
  serverSideData: ConsiderationPageData;
};

type ConsiderationLandingPageTemplateProps = ConsiderationLandingPageProps & {
  slug: ConsiderationPageSlug;
};

const PAGES_WITHOUT_CONTACT: ConsiderationPageSlug[] = [
  CONSIDERATION_CONTENT_SLUGS.AWARDS_LANDING_PAGE,
  CONSIDERATION_CONTENT_SLUGS.PREGNANCY_LANDING_PAGE,
];

const ConsiderationLandingPageTemplate = ({
  activeOffers: serverSideOffers,
  placeholders: serverSidePlaceholders,
  serverSideData,
  slug,
}: ConsiderationLandingPageTemplateProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const { activeOffers, placeholders, loading: isOfferContentLoading } = useGetOffersContent({ shouldSkip });

  const { items: contactItems, loading: isLoadingContact } = useTableOfContents({
    query: GET_TOC_CONTACTS,
    skip: shouldSkip,
    slug: SLUG.LOOKING_TO_JOIN_CONTACTS,
    typename: 'Contact',
  });

  const { considerationContent: considerationContentData, isLoading: isLoadingConsiderationContent } =
    useGetConsiderationContent({
      skip: shouldSkip,
      slug,
    });

  const isPartnerReferral = useSelector(selectHasPartnerReferrer);
  const targetSegment = useSelector(selectTargetSegment);
  const referralCode = useSelector(selectReferralCode);

  const isLoading = isLoadingConsiderationContent || isLoadingContact;

  const clientSideData: ConsiderationPageData = PAGES_WITHOUT_CONTACT.includes(slug)
    ? {
        considerationContent: considerationContentData,
      }
    : {
        considerationContent: considerationContentData,
        contact: {
          items: contactItems as TableOfContentsItem[],
          name: getContactTitle(slug),
        },
      };

  const cmsData = isLoading || shouldSkip ? serverSideData : clientSideData;

  const offersContent =
    shouldSkip || isOfferContentLoading
      ? {
          activeOffers: serverSideOffers,
          placeholders: serverSidePlaceholders,
        }
      : {
          activeOffers,
          placeholders,
        };

  const priorityProductType = cmsData.considerationContent?.productType?.identifier?.current;

  const filteredOffer = filterOffersByProductType(priorityProductType, offersContent.activeOffers);

  const offerProps = {
    overrideOffers: referralCode ? undefined : compact([filteredOffer]),
    placeholders: offersContent.placeholders,
    targetSegment,
  };

  const offerContentToRender = filteredOffer && !isPartnerReferral ? offerProps : null;
  const considerationContent = getContentToRender({ cmsData, offerContentToRender, slug });
  return <ConsiderationLandingPage content={considerationContent} />;
};

type GetTemplateServerSideProps = {
  slug: ConsiderationPageSlug;
};
export const getTemplateServerSideProps = ({ slug }: GetTemplateServerSideProps) =>
  nextReduxWrapper.getServerSideProps((store) => async ({ req, res }) => {
    setCacheControlHeader(res);
    const { dataset, mode } = getPreviewOptionsFromCookieHeader(req.headers.cookie) ?? {};

    const shouldFetchContacts = !PAGES_WITHOUT_CONTACT.includes(slug);

    const considerationContentPromise = getCmsData(
      GET_CONSIDERATION_CONTENT,
      { variables: { slug } },
      dataset,
      mode,
    ).then(async (considerationContentData) => {
      const considerationContent = head(considerationContentData.allConsiderationContent) as
        | ConsiderationContent
        | undefined;

      if (!considerationContent) {
        throw new Error(`No consideration content found for slug: ${slug}`);
      }

      const productListSections = getSectionsByTypename(considerationContent.refersToContent, 'ProductListSection');
      const productIds = uniq([
        ...getSaleableProductIdsFromProductListSections(productListSections),
        ...getCustomSections(slug).flatMap((section) => section.productIds ?? []),
      ]);
      if (!productIds.length) {
        return considerationContent;
      }

      await fetchProductsFromParams(store.getState(), store.dispatch, { productIds });
      return considerationContent;
    });

    const [considerationContent, contactData, { activeOffers, placeholders }] = await Promise.all([
      considerationContentPromise,
      shouldFetchContacts
        ? getCmsData(
            GET_TOC_CONTACTS,
            {
              variables: {
                identifier: SLUG.LOOKING_TO_JOIN_CONTACTS,
              },
            },
            dataset,
            mode,
          )
        : null,
      getOffersContent(dataset, mode),
    ]);

    const contactList = head(contactData?.allTableOfContentsList) || null;
    const items = (contactList?.items ?? []) as TableOfContentsItem[];

    const cmsData: ConsiderationPageData = isEmpty(items)
      ? {
          considerationContent,
        }
      : {
          considerationContent,
          contact: {
            items,
            name: getContactTitle(slug),
          },
        };

    return {
      props: {
        activeOffers,
        placeholders,
        serverSideData: cmsData,
      },
    };
  });

export default ConsiderationLandingPageTemplate;
